// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 41-groups-management.md

import * as React from 'react';
import { Building2, Mail, User, Users } from 'lucide-react';
import type { AppAccessCandidate } from '@greenlight/shared';

import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { cn } from '@/lib/utils';

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;
const LISTBOX_ID = 'access-search-results';

/** Someone the org has no user record for; adding them is still worth offering. */
export interface EmailCandidate {
  kind: 'email';
  email: string;
}

export type AccessSelection = AppAccessCandidate | EmailCandidate;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// One key space for "already on the list", written once here because the search
// builds it from candidates and the list builds it from rows.
export const personKey = (userId: string): string => `person:${userId}`;
export const emailKey = (email: string): string => `email:${email.toLowerCase()}`;
export const groupKey = (groupId: string): string => `group:${groupId}`;
export const idpGroupKey = (registrationId: string): string => `idp:${registrationId}`;

// A person is matched by either key: a grant row carries both, and an address
// typed in full has no user id to compare.
const candidateKeys = (c: AccessSelection): string[] => {
  switch (c.kind) {
    case 'person':
      return [personKey(c.user_id), emailKey(c.email)];
    case 'group':
      return [groupKey(c.group_id)];
    case 'idp_group':
      return [idpGroupKey(c.idp_group_registration_id)];
    case 'email':
      return [emailKey(c.email)];
  }
};

const candidateKey = (c: AccessSelection): string => candidateKeys(c)[0] as string;

const members = (count: number): string => `${count} ${count === 1 ? 'member' : 'members'}`;

const KIND_LABEL: Record<AccessSelection['kind'], string> = {
  person: 'Person',
  group: 'Greenlight group',
  idp_group: 'Directory group',
  email: 'Person',
};

const KIND_ICON = { person: User, group: Users, idp_group: Building2, email: Mail } as const;

/**
 * One control over all three kinds an app's access can name (docs/41 §UI).
 * There is no second field and no identifier input: a directory group reaches
 * this list by the label IT registered, so an owner never types or sees a GUID.
 */
export function AccessSearch({
  appId,
  onSelect,
  disabled,
  /** Already on the list, so the search does not offer to add them twice. */
  excludeKeys,
}: {
  appId: string;
  onSelect: (candidate: AccessSelection) => void;
  disabled?: boolean;
  excludeKeys: ReadonlySet<string>;
}): React.ReactElement {
  const [term, setTerm] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const debounced = useDebouncedValue(term.trim(), DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_QUERY;

  const searchKey = ['access-candidates', appId, debounced] as const;
  const search = api.rbac.searchCandidates.useQuery(
    searchKey,
    { params: { id: appId }, query: { q: debounced } },
    { queryKey: searchKey, enabled, staleTime: 15_000 },
  );

  const results = React.useMemo((): AccessSelection[] => {
    const found: AccessSelection[] =
      search.data?.status === 200
        ? search.data.body.items.filter((c) => !candidateKeys(c).some((k) => excludeKeys.has(k)))
        : [];
    // A colleague the search did not match is still addable by a full address,
    // which is how this control worked before it resolved anything.
    const typed = debounced.toLowerCase();
    const unmatchedEmail =
      EMAIL_PATTERN.test(typed) &&
      !found.some((c) => c.kind === 'person' && c.email.toLowerCase() === typed) &&
      !excludeKeys.has(emailKey(typed));
    return unmatchedEmail ? [...found, { kind: 'email', email: typed }] : found;
  }, [search.data, excludeKeys, debounced]);

  React.useEffect(() => setActive(0), [results.length, debounced]);

  const choose = (candidate: AccessSelection): void => {
    onSelect(candidate);
    setTerm('');
    setOpen(false);
  };

  const showList = open && enabled;
  const optionId = (index: number): string => `${LISTBOX_ID}-option-${index}`;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!showList || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = results[active];
      if (picked) choose(picked);
    }
  };

  return (
    <div className="relative flex-1">
      <Input
        type="text"
        role="combobox"
        value={term}
        placeholder="Add people or groups"
        aria-label="Add people or groups"
        aria-expanded={showList}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={showList && results.length > 0 ? optionId(active) : undefined}
        autoComplete="off"
        disabled={disabled}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
        data-testid="access-search-input"
        className="h-11 md:h-9"
      />
      {showList && (
        <ul
          id={LISTBOX_ID}
          role="listbox"
          aria-label="People and groups matching your search"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-gl-lg border border-border/70 bg-popover p-1 shadow-md"
          data-testid="access-search-results"
        >
          {results.map((candidate, index) => {
            const Icon = KIND_ICON[candidate.kind];
            return (
              <li
                key={candidateKey(candidate)}
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                // Selecting before blur, so the click is not cancelled by the
                // list unmounting on focus loss.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(candidate);
                }}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-gl px-2.5 py-2 text-[13px]',
                  index === active && 'bg-accent',
                )}
              >
                <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate font-medium">
                  {candidate.kind === 'person'
                    ? (candidate.display_name ?? candidate.email)
                    : candidate.kind === 'email'
                      ? candidate.email
                      : candidate.name}
                </span>
                <span className="shrink-0 text-[12px] text-muted-foreground">
                  {candidate.kind === 'person'
                    ? candidate.email
                    : candidate.kind === 'email'
                      ? KIND_LABEL.email
                      : `${KIND_LABEL[candidate.kind]} · ${members(candidate.member_count)}`}
                </span>
              </li>
            );
          })}
          {results.length === 0 && (
            <li
              className="px-2.5 py-2 text-[12px] text-muted-foreground"
              data-testid="access-search-empty"
            >
              {search.isFetching
                ? 'Searching…'
                : 'Nobody and no group matches. IT creates groups and registers directory groups under Users & RBAC.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export { members };
